import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  useEntityLoraModels,
  useQueueTraining,
  useCancelTraining,
  useRequeueTraining,
  type QueueTrainingRequest,
} from '../../api/queries/lora';
import { useTrainingProgress } from '../../hooks/useTrainingProgress';
import { LoraModelCard } from './LoraModelCard';
import { TrainLoraDialog } from './TrainLoraDialog';
import { LoraModelDetailDrawer } from './LoraModelDetailDrawer';
import { LoraTestModal } from './LoraTestModal';
import type { EntityDto } from '../../api/queries/entities';

interface Props {
  entity: EntityDto;
}

/**
 * LoRA Models tab on the entity detail page. Shows all trained models with
 * live progress, and a "Train New Model" action that queues training directly
 * to the GPU pipeline (no admin approval needed).
 */
export function EntityLoraPanel({ entity }: Props) {
  const [trainDialogOpen, setTrainDialogOpen] = useState(false);
  const [selectedLoraId, setSelectedLoraId] = useState<string | null>(null);
  const [testModel, setTestModel] = useState<{
    id: string;
    triggerWord: string;
  } | null>(null);

  const models = useEntityLoraModels(entity.id);
  const queueTraining = useQueueTraining(entity.id);
  const cancelTraining = useCancelTraining(entity.id);
  const requeueTraining = useRequeueTraining(entity.id);

  const activeModel = models.data?.find((m) => m.trainingStatus === 'training');
  const { event: liveProgress } = useTrainingProgress(activeModel?.id ?? null);

  const handleQueueTraining = (request: QueueTrainingRequest) => {
    queueTraining.mutate(request, {
      onSuccess: () => setTrainDialogOpen(false),
    });
  };

  if (models.isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">LoRA Models</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={() => setTrainDialogOpen(true)}
          disabled={
            entity.images.length === 0 && entity.descriptors.length === 0
          }
        >
          Train New Model
        </Button>
      </Stack>

      {entity.images.length === 0 && entity.descriptors.length === 0 && (
        <Alert severity="info">
          Add reference images or descriptors before training a LoRA model.
        </Alert>
      )}

      {entity.images.length === 0 && entity.descriptors.length > 0 && (
        <Alert severity="warning">
          Training with descriptors only — the pipeline will generate reference
          images from your descriptors. Adding images improves quality.
        </Alert>
      )}

      {entity.images.length > 0 && entity.images.length < 5 && (
        <Alert severity="warning">
          Only {entity.images.length} reference image
          {entity.images.length !== 1 ? 's' : ''} — at least 5 recommended for
          quality results.
        </Alert>
      )}

      {models.data?.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: 'center' }}
        >
          No LoRA models trained yet. Click "Train New Model" to get started.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {models.data?.map((model) => (
            <LoraModelCard
              key={model.id}
              model={model}
              liveProgress={
                liveProgress?.loraModelId === model.id ? liveProgress : null
              }
              onCancel={(id) => cancelTraining.mutate(id)}
              onRequeue={(id) => requeueTraining.mutate(id)}
              onViewDetail={(id) => setSelectedLoraId(id)}
            />
          ))}
        </Stack>
      )}

      <TrainLoraDialog
        open={trainDialogOpen}
        onClose={() => setTrainDialogOpen(false)}
        onSubmit={handleQueueTraining}
        entityName={entity.name}
        imageCount={entity.images.length}
        descriptorCount={entity.descriptors.length}
        isSubmitting={queueTraining.isPending}
      />

      <LoraModelDetailDrawer
        entityId={entity.id}
        loraId={selectedLoraId}
        onClose={() => setSelectedLoraId(null)}
        onTest={(id, triggerWord) => {
          setSelectedLoraId(null);
          setTestModel({ id, triggerWord });
        }}
      />

      {testModel && (
        <LoraTestModal
          open
          onClose={() => setTestModel(null)}
          triggerWord={testModel.triggerWord}
          entityName={entity.name}
          entityId={entity.id}
          loraModelId={testModel.id}
        />
      )}
    </Stack>
  );
}
